import { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Person from "@mui/icons-material/Person";
import Print from "@mui/icons-material/Print";
import CloudDownload from "@mui/icons-material/CloudDownload";
import AccessTime from "@mui/icons-material/AccessTime";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import TimerOutlined from "@mui/icons-material/TimerOutlined";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardOutlined from "@mui/icons-material/KeyboardOutlined";
import MicNoneOutlined from "@mui/icons-material/MicNoneOutlined";
import AppsOutlined from "@mui/icons-material/AppsOutlined";
import RadioButtonCheckedOutlined from "@mui/icons-material/RadioButtonCheckedOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import RemoveCircleOutlined from "@mui/icons-material/RemoveCircleOutlined";
import TheaterComedyOutlined from "@mui/icons-material/TheaterComedyOutlined";

import FeedbackIcon from "@mui/icons-material/SpeakerNotes";
import { makeStyles } from "tss-react/mui";
import moment from "moment";
import { generateCsv, mkConfig, download } from "export-to-csv";
import AnswersBreakdownChart from "@nualang/nualang-ui-components/dist/Charts/Doughnut/AnswersBreakdown";
import AnswerMethodChart from "@nualang/nualang-ui-components/dist/Charts/Doughnut/AnswerMethod";
import Chat from "@nualang/nualang-ui-components/dist/Chat/Chat";
import AttemptTable from "@nualang/nualang-ui-components/dist/Tables/Attempt/Attempt";
import ImageIcon from "@mui/icons-material/Image";
import UnansweredTable from "@nualang/nualang-ui-components/dist/Tables/Unanswered/Unanswered";
import ShareButton from "@nualang/nualang-ui-components/dist/Misc/ShareButton";
import SessionReportCard from "@nualang/nualang-ui-components/dist/Cards/SessionReportCard/SessionReportCard";
import { collectCorrections } from "@nualang/nualang-ui-components/dist/utils/correction";
import UpgradeSubscription from "@nualang/nualang-ui-components/dist/Dialogs/UpgradeSubscription/UpgradeSubscription";
import AddFeedbackDialog from "@nualang/nualang-ui-components/dist/Dialogs/Feedback/FeedbackDialog";
import UpdateFeedbackDialog from "@nualang/nualang-ui-components/dist/Dialogs/Feedback/FeedbackDialog";
import IconButton from "@mui/material/IconButton";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import Button from "@nualang/nualang-ui-components/dist/Misc/DefaultColourButton";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { DateTime } from "luxon";
import useExerciseState from "@nualang/nualang-ui-components/dist/hooks/useExerciseState";

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  cardStyle: {
    whiteSpace: "nowrap",
    overflowX: "auto",
  },
  padding: {
    padding: theme.spacing(2, 3),
  },
  contentWrapper: {
    overflow: "auto",
    maxHeight: 700,
    "@media print": {
      overflow: "visible",
      maxHeight: "none",
    },
  },
  fullHeightCard: {
    height: "100%",
    "@media print": {
      height: "auto",
      width: "100%",
      border: 0,
    },
  },
  fullHeightCardNoPrint: {
    height: "100%",
    "@media print": {
      display: "none",
    },
  },
  header: {
    display: "flex",
    alignItems: "start",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(),
  },
  headerText: {
    flexGrow: 1,
  },
  buttons: {
    padding: theme.spacing(1),
    justifyContent: "space-between",
    textAlign: "left",
    "@media print": {
      display: "none",
    },
  },
  button: {
    margin: theme.spacing(2, 1, 2, 0),
  },
  card: {
    "@media print": {
      border: 0,
      backgroundColor: "#ffff",
      boxShadow: "none",
      overflow: "visible",
    },
  },
  feedbackcard: {
    marginBottom: "10px",
  },
  avatarHeader: {
    height: 40,
    width: 40,
    marginRight: theme.spacing(2),
  },
}));

function AttemptSummaryBar({ items }) {
  // Two columns on small screens, so an odd count leaves one item alone on the last row.
  const smLastRowStart = items.length - (items.length % 2 === 0 ? 2 : 1);
  return (
    <Card data-cy='attempt-summary' sx={{ width: "100%", mb: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: `repeat(${items.length}, 1fr)` },
        }}
      >
        {items.map(({ key, icon: Icon, label, value }, index) => (
          <Box
            key={key}
            data-cy={`attempt-summary-${key}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 1.5,
              borderStyle: "solid",
              borderColor: "divider",
              borderWidth: 0,
              borderRightWidth: {
                xs: 0,
                sm: index % 2 === 0 && index < items.length - 1 ? 1 : 0,
                md: index < items.length - 1 ? 1 : 0,
              },
              borderBottomWidth: {
                xs: index < items.length - 1 ? 1 : 0,
                sm: index < smLastRowStart ? 1 : 0,
                md: 0,
              },
            }}
          >
            <Icon color='primary' sx={{ fontSize: 28, flex: "none" }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='body2' color='text.secondary'>
                {label}
              </Typography>
              <Typography variant='subtitle1' sx={{ fontWeight: 700, lineHeight: 1.3 }} noWrap>
                {value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

const OTHER_SEGMENT_STYLE = { icon: HelpOutlineOutlined, color: "text.disabled" };

const METHOD_STYLES = {
  text: { icon: KeyboardOutlined, color: "primary.main" },
  voice: { icon: MicNoneOutlined, color: "warning.main" },
  "word_bank": { icon: AppsOutlined, color: "info.main" },
  option: { icon: RadioButtonCheckedOutlined, color: "secondary.main" },
  "multiple_choice": { icon: RadioButtonCheckedOutlined, color: "secondary.main" },
};

const RESULT_STYLES = {
  correct: { icon: CheckCircleOutlined, color: "success.main" },
  incorrect: { icon: CancelOutlined, color: "error.main" },
  "partially_correct": { icon: RemoveCircleOutlined, color: "warning.main" },
};

// Exercises whose answers have no meaningful input method to report.
const NO_METHOD_EXERCISES = [
  "pronunciation",
  "meaning",
  "meaning-listen-and-speak",
  "meaning-read-and-write",
  "roleplay-story",
  "roleplay-fill-in-the-blanks",
  "roleplay-act-it-out",
  "roleplay-act-it-out-listening",
  "roleplay-act-it-out-speaking",
  "roleplay-act-it-out-listening-speaking",
];

// Counts items by `field`, keeping `alwaysShow` keys first (even at 0) and then any others in order seen.
function countSegments(items, field, styles, alwaysShow = []) {
  const counts = new Map(alwaysShow.map((key) => [key, 0]));
  items.forEach((item) => {
    const key = item?.[field];
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  });
  return Array.from(counts, ([key, count]) => ({
    key,
    count,
    ...(styles[key] || OTHER_SEGMENT_STYLE),
  }));
}

function StackedCountSummary({ label, segments, dataCy, itemDataCy, t }) {
  const total = segments.reduce((sum, { count }) => sum + count, 0);

  return (
    <Box
      data-cy={dataCy}
      sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 2.5, rowGap: 1 }}
    >
      <Typography variant='body1' color='text.secondary'>
        {label}
      </Typography>
      <Box
        aria-hidden='true'
        sx={{
          display: "flex",
          width: { xs: 140, sm: 200, md: 250 },
          height: 10,
          borderRadius: 5,
          overflow: "hidden",
          bgcolor: "action.disabledBackground",
        }}
      >
        {total > 0 &&
          segments.map(({ key, count, color }) => (
            <Box key={key} sx={{ width: `${(count / total) * 100}%`, bgcolor: color }} />
          ))}
      </Box>
      {segments.map(({ key, count, icon: Icon, color }) => (
        <Box
          key={key}
          data-cy={`${itemDataCy}-${key}`}
          sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
        >
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color }} />
          <Icon fontSize='small' sx={{ color: "text.secondary" }} />
          <Typography variant='body1'>
            {t(key)}{" "}
            <Box component='span' sx={{ fontWeight: 700 }}>
              {count}
            </Box>
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function AnswerMethodSummary({ items = [], alwaysShow, t }) {
  return (
    <StackedCountSummary
      label={t("answer_method")}
      segments={countSegments(items, "method", METHOD_STYLES, alwaysShow)}
      dataCy='answer-method-summary'
      itemDataCy='answer-method'
      t={t}
    />
  );
}

function AnswerResultsSummary({ answerAttempts = [], t }) {
  return (
    <StackedCountSummary
      label={t("answers")}
      segments={countSegments(answerAttempts, "result", RESULT_STYLES)}
      dataCy='answer-results-summary'
      itemDataCy='answer-result'
      t={t}
    />
  );
}

function CardTitleBar({ title, children, actions }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 3,
        rowGap: 1,
        px: 2,
        py: 1,
      }}
    >
      <Typography variant='h5' component='h3'>
        {title}
      </Typography>
      {children && (
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: 1 },
            order: { xs: 3, md: 0 },
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      )}
      {actions && (
        <Box sx={{ ml: { xs: "auto", md: 0 }, displayPrint: "none" }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

export default function Attempt({
  t = (text) => text,
  exerciseAttempt = {
    exercise: "",
    answerAttempts: [],
  },
  handleSpeak,
  handleTranslate,
  subscription = {
    isUpgradePossible: true,
  },
  handleFeedbackSubmit,
  studentName,
  isCreator,
  feedbackCreaterName,
  feedbackCreatedDate,
  feedback,
  userImage,
  handleFeedbackDelete,
  handleFeedbackUpdate,
  feedbackUpdatedDate,
  mainSiteUrl,
  addAudioListToQueue,
  highlightSpeechText,
  clearQueue,
  stop,
  handleStopSpeaking,
  learnLang,
  sessionReport,
  sessionReportStorageKey,
  sessionReportNewStorageKey,
}) {
  const { classes } = useStyles();

  const {
    createdByName,
    exercise,
    exerciseStartTime,
    createdAt,
    picture,
    answerAttempts,
    selectedActor: selectedActorNumber,
    actor1,
    actor2,
    messages,
    vars,
    description,
    topicName,
    botName,
    roleplayName,
    unansweredPhrases,
    unansweredQuestions,
  } = exerciseAttempt;
  const timeSpent = createdAt - exerciseStartTime;
  const isBot = exercise === "bot" || exercise === "bot-listen-and-speak";
  const showsRolePlayed =
    exercise === "roleplay" || Boolean(exercise?.startsWith("roleplay-act-it-out"));
  const validAnswerAttempts = useMemo(
    () => (answerAttempts || []).filter((a) => a),
    [answerAttempts],
  );
  const studentMessages = useMemo(
    () => (messages || []).filter((m) => m?.isMyMessage),
    [messages],
  );
  // Same corrections the completion dialog listed, shown with the conversation feedback.
  const sessionReportCorrections = useMemo(
    () => collectCorrections(messages),
    [messages],
  );
  const attemptTime = moment(exerciseStartTime).format("HH:mm:ss");
  const attemptDate = moment(exerciseStartTime).format("DD/MM/Y");
  const [selectedActor, setSelectedActor] = useState("");
  const messagesRef = useRef(null);

  const scrollToMessages = () => {
    if (messagesRef.current) {
      messagesRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const filteredVarKeys =
    vars &&
    Object.keys(vars).length > 0 &&
    Object.keys(vars).filter((v) => !v.startsWith("__"));

  const [open, setOpen] = useState(false);
  const [isTranscriptPlaying, setIsTranscriptPlaying] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const exerciseState = useExerciseState({
    t,
    open: false,
    handleClose,
    handleSpeak,
    handleStopSpeaking,
    learnLang,
  });
  const { speak, stopSpeaking, isSpeaking, isSpeakingTextContainer } =
    exerciseState;

  const [confirm] = useConfirm(t);

  const deleteFeedback = async () => {
    const confirmed = await confirm(
      t("delete_feedback"),
      t("delete_feedback_confirmation"),
    );

    if (confirmed) {
      await handleFeedbackDelete();
    }
  };

  useEffect(() => {
    if (selectedActorNumber === 1) {
      setSelectedActor(actor1);
    } else if (selectedActorNumber === 2) {
      setSelectedActor(actor2);
    }
  }, [selectedActorNumber, actor1, actor2]);

  const addLineDataCSV = (arrVals = []) => {
    return {
      col000: arrVals[0] === undefined ? "" : arrVals[0],
      col001: arrVals[1] === undefined ? "" : arrVals[1],
      col002: arrVals[2] === undefined ? "" : arrVals[2],
      col003: arrVals[3] === undefined ? "" : arrVals[3],
      col004: arrVals[4] === undefined ? "" : arrVals[4],
      col005: arrVals[5] === undefined ? "" : arrVals[5],
      col006: arrVals[6] === undefined ? "" : arrVals[6],
      col007: arrVals[7] === undefined ? "" : arrVals[7],
      col008: arrVals[8] === undefined ? "" : arrVals[8],
      col009: arrVals[9] === undefined ? "" : arrVals[9],
      col010: arrVals[10] === undefined ? "" : arrVals[10],
      col011: arrVals[11] === undefined ? "" : arrVals[11],
      col012: arrVals[12] === undefined ? "" : arrVals[12],
      col013: arrVals[13] === undefined ? "" : arrVals[13],
      col014: arrVals[14] === undefined ? "" : arrVals[14],
      col015: arrVals[15] === undefined ? "" : arrVals[15],
      col016: arrVals[16] === undefined ? "" : arrVals[16],
      col017: arrVals[17] === undefined ? "" : arrVals[17],
      col018: arrVals[18] === undefined ? "" : arrVals[18],
      col019: arrVals[19] === undefined ? "" : arrVals[19],
    };
  };

  const handleDownloadHead = (data) => {
    if (!exercise) return data;

    data.push(addLineDataCSV([t("exercise_info").toUpperCase()]));
    data.push(addLineDataCSV([t("topic").toUpperCase() + ":", topicName]));
    data.push(
      addLineDataCSV([t("description").toUpperCase() + ":", description]),
    );
    data.push(addLineDataCSV([t("exercise").toUpperCase() + ":", exercise]));
    if (roleplayName || botName) {
      data.push(
        addLineDataCSV([
          t("name").toUpperCase() + ":",
          roleplayName || botName,
        ]),
      );
    }
    data.push(addLineDataCSV([])); // blank line

    data.push(addLineDataCSV([t("records").toUpperCase()]));
    data.push(addLineDataCSV([t("name").toUpperCase() + ":", createdByName]));
    data.push(
      addLineDataCSV([
        t("time_spent").toUpperCase() + ":",
        timeSpent && timeSpent > 0 && moment.duration(timeSpent).humanize(),
      ]),
    );
    data.push(addLineDataCSV([t("date").toUpperCase() + ":", attemptDate]));
    data.push(addLineDataCSV([t("time").toUpperCase() + ":", attemptTime]));

    data.push(addLineDataCSV([])); // blank line
    return data;
  };
  const handleDownloadGeneral = (data) => {
    if (!exercise) return data;
    if (NO_METHOD_EXERCISES.includes(exercise)) return data;

    // answer_method
    data.push(addLineDataCSV([t("answer_method").toUpperCase()]));

    const attemptMap = new Map();
    const methods = (answerAttempts || messages || []).filter(
      (a) => a && a.method,
    );
    methods.map((attempt) => {
      attemptMap.set(
        attempt.method,
        attemptMap.get(attempt.method) ? attemptMap.get(attempt.method) + 1 : 1,
      );
      return false;
    });

    const attempLabels = Array.from(attemptMap.keys()).map((l) => t(l));
    const attemptData = Array.from(attemptMap.values());
    attempLabels.map((itemLabel, i) =>
      data.push(addLineDataCSV([itemLabel, attemptData[i]])),
    );

    return data;
  };
  const handleDownloadAttemptTable = (data) => {
    if (exercise === "bot" || exercise === "bot-listen-and-speak") return data;

    data.push(addLineDataCSV([])); // blank line

    data.push(addLineDataCSV([t("attempt_table").toUpperCase()]));

    const head = [
      t("result").toUpperCase(),
      t("attempt").toUpperCase(),
      t("answer").toUpperCase(),
    ];

    if (exercise === "roleplay") {
      head.push(t("question").toUpperCase());
      head.push(t("type").toUpperCase());
    } else if (!NO_METHOD_EXERCISES.includes(exercise)) {
      head.push(t("method").toUpperCase());
    }
    data.push(addLineDataCSV(head));

    let correctTotal = 0;
    let incorrectTotal = 0;
    let skipped = 0;

    for (let i = 0; i < answerAttempts.length; i++) {
      const item = answerAttempts[i];
      switch (item.result) {
        case "correct":
          correctTotal += 1;
          break;
        case "incorrect":
          incorrectTotal += 1;
          break;
        case "skipped":
          skipped += 1;
          break;

        default:
          break;
      }

      const bodyLine = [
        item.result ? t(item.result) : "",
        item.attempt ? item.attempt : "",
        item.answer ? item.answer : "",
      ];

      if (exercise === "roleplay") {
        bodyLine.push(item?.question ? item.question : "");
        bodyLine.push(item.type ? item.type : "");
      } else if (!NO_METHOD_EXERCISES.includes(exercise)) {
        bodyLine.push(item.method ? item.method : "");
      }
      data.push(addLineDataCSV(bodyLine));
    }

    data.push(addLineDataCSV([])); // blank line

    // TOTAL
    data.push(addLineDataCSV([t("answers").toUpperCase()]));
    data.push(addLineDataCSV([t("correct"), t("incorrect"), t("skipped")]));
    data.push(addLineDataCSV([correctTotal, incorrectTotal, skipped]));
    if (feedback && feedbackCreaterName && feedbackCreatedDate) {
      data.push(
        addLineDataCSV([
          t("feedback").toUpperCase() + ":",
          feedback,
          t("from").toUpperCase() + ":",
          feedbackCreaterName,
          t("on").toUpperCase() + ":",
          DateTime.fromMillis(feedbackCreatedDate).toFormat("dd LLL yyyy"),
        ]),
      );
    }
    data.push(addLineDataCSV([])); // blank line
    return data;
  };
  const handleDownloadBot = (data) => {
    if (exercise !== "bot" && exercise !== "bot-listen-and-speak") return data;

    if (filteredVarKeys && filteredVarKeys.length > 0) {
      data.push(addLineDataCSV([t("bot_outputs").toUpperCase()]));
      filteredVarKeys.map((key) => data.push(addLineDataCSV([key, vars[key]])));
    }

    data.push(addLineDataCSV([])); // blank line
    if (messages && messages.length > 0) {
      data.push(addLineDataCSV([t("attempt_messages").toUpperCase()]));
      data.push(
        addLineDataCSV([
          t("datetime").toUpperCase(),
          t("name").toUpperCase(),
          t("text").toUpperCase(),
          t("correction_original").toUpperCase(),
          t("correction_suggested").toUpperCase(),
        ]),
      );

      // Corrections sit on the student's own message, so they have to be exported as their
      // own columns or they are silently missing from the download.
      messages.map((item) =>
        data.push(
          addLineDataCSV([
            item.datetime,
            item.name,
            item.text,
            item.correction?.original ?? "",
            item.correction?.corrected ?? "",
          ]),
        ),
      );
    }

    data.push(addLineDataCSV([])); // blank line
    return data;
  };
  const handleDownloadRoleplay = (data) => {
    if (exercise !== "roleplay") return data;

    data.push(
      addLineDataCSV([
        t("role_played").toUpperCase() + ":",
        selectedActor.name,
      ]),
    );

    data = handleDownloadAttemptTable(data);
    return data;
  };
  const handleDownloadListening = (data) => {
    if (exercise !== "listening") return data;

    data = handleDownloadAttemptTable(data);
    return data;
  };
  const handleDownloadTranslation = (data) => {
    if (exercise !== "translation") return data;

    data = handleDownloadAttemptTable(data);
    return data;
  };
  const handleDownloadPronunciation = (data) => {
    if (exercise !== "pronunciation") return data;

    data = handleDownloadAttemptTable(data);
    return data;
  };

  const handleDownloadAsExcelCSV = () => {
    let data = [];
    data = handleDownloadHead(data);
    data = handleDownloadGeneral(data);
    data = handleDownloadBot(data);
    data = handleDownloadRoleplay(data);
    data = handleDownloadListening(data);
    data = handleDownloadTranslation(data);
    data = handleDownloadPronunciation(data);

    const label = t("attempt_info").replace(" ", "_");
    const nowSheet = DateTime.now().toFormat("f");
    const title = `${label.toUpperCase()} - ${t(
      exercise,
    ).toUpperCase()}, ${nowSheet}`;

    const nowFile = DateTime.now().toFormat("dd LLL yyyy");
    const filename = `${label.toLowerCase()}_${t(
      exercise,
    ).toLowerCase()}_${nowFile}`;
    downloadCSV(data, title, filename);
  };

  const downloadCSV = (data, title, filename) => {
    // Print configuration
    const options = {
      fieldSeparator:
        exercise === "bot" || exercise === "bot-listen-and-speak" ? "," : ";",
      quoteStrings: true,
      decimalSeparator: ".",
      showColumnHeaders: false,
      useKeysAsHeaders: true,
      showTitle: true,
      title: title,
      useTextFile: false,
      useBom: true,
      filename,
    };
    const csvConfig = mkConfig(options);
    const csv = generateCsv(csvConfig)(data);
    download(csvConfig)(csv);
  };

  const handlePrint = () => {
    window.print();
  };

  const [isUpgradeSubscriptionOpen, setIsUpgradeSubscriptionOpen] =
    useState(false);
  // const handleOpenUpgradeSubscriptionModal = () => {
  //   setIsUpgradeSubscriptionOpen(true);
  // }
  const handleCloseUpgradeSubcriptionModal = () => {
    setIsUpgradeSubscriptionOpen(false);
  };
  const handleSubscriptionPage = () => {
    window.open(`${mainSiteUrl}/pricing/app`, "_blank");
  };

  const handlePlayTranscript = async () => {
    setIsTranscriptPlaying(true);
    const audioList = await Promise.all(
      messages.map(async (m, i) => {
        const isLastMessage = messages.length === i + 1;
        if (m.voiceAttempt) {
          return {
            binary: m.voiceAttempt,
            callback: () => {
              if (isLastMessage) {
                setIsTranscriptPlaying(false);
              }
            },
            highlightCallback: () => {},
          };
        } else {
          const result = await handleSpeak({
            text: m.text,
            // learnLanguage: learnLang,
            voiceName: m.voice,
            languageCode: m.voiceLanguageCode,
            // voiceSpeed,
            mute: true,
            callback: () => {},
            textContainer: null,
          });
          const highlightCallback = () =>
            highlightSpeechText(`#message${i}`, result.speechMarks);
          return {
            binary: result.audioUrl,
            callback: () => {
              if (isLastMessage) {
                setIsTranscriptPlaying(false);
              }
            },
            highlightCallback,
          };
        }
      }),
    );

    addAudioListToQueue(audioList);
  };

  const handleStopTranscript = () => {
    clearQueue();
    stop();
    setIsTranscriptPlaying(false);
  };

  const hasInappropriate = useMemo(() => {
    if (!messages || messages.length === 0) {
      return false;
    }
    return (messages || []).some((m) => {
      return m?.studentInappropriate;
    });
  }, [messages]);

  return (
    <div className={classes.root}>
      {hasInappropriate && (
        <div
          style={{
            backgroundColor: "#ffcccc",
            padding: "10px",
            color: "#660000",
            fontWeight: "bold",
            border: "1px solid #cc0000",
            cursor: "pointer", // make it look clickable
          }}
          onClick={scrollToMessages}
        >
          ⚠ {t("student_sent_inappropriate_messages")}
        </div>
      )}
      {(picture || topicName || description) && (
        <div className={classes.header}>
          {picture ? (
            <Avatar
              className={classes.avatarHeader}
              src={`${picture}`}
              alt=''
            />
          ) : (
            <Avatar className={classes.avatarHeader} alt=''>
              <ImageIcon />
            </Avatar>
          )}
          <div className={classes.headerText}>
            <Typography variant='h5' component='h2'>
              {topicName !== "topic_name_not_known" && topicName !== undefined
                ? topicName +
                  (roleplayName || botName
                    ? " - " + (roleplayName || botName)
                    : "")
                : roleplayName || botName
                  ? roleplayName || botName
                  : ""}
            </Typography>
            <Typography variant='body2' color='textSecondary' gutterBottom>
              {description}
            </Typography>
            <div className={classes.buttons}>
              <ShareButton
                t={t}
                url={window.location.href}
                title={
                  topicName +
                  (roleplayName || botName
                    ? " - " + (roleplayName || botName)
                    : "")
                }
                className={classes.button}
                subscription={subscription}
                mainSiteUrl={mainSiteUrl}
              />
              <Button
                data-cy='attempt-download-button'
                className={classes.button}
                variant='contained'
                startIcon={<CloudDownload />}
                size='small'
                onClick={() => handleDownloadAsExcelCSV()}
              >
                {t("download_csv_file")}
              </Button>
              <Button
                className={classes.button}
                variant='contained'
                startIcon={<Print />}
                size='small'
                onClick={() => handlePrint()}
              >
                {t("print")}
              </Button>
              {isCreator ? (
                <>
                  <Button
                    className={classes.button}
                    variant='contained'
                    startIcon={<FeedbackIcon />}
                    size='small'
                    onClick={handleClickOpen}
                    disabled={
                      isCreator &&
                      feedback &&
                      feedbackCreatedDate &&
                      feedbackCreaterName
                        ? true
                        : false
                    }
                  >
                    {t("write_feedback")}
                  </Button>
                  <AddFeedbackDialog
                    open={open}
                    t={t}
                    handleClose={handleClose}
                    studentName={studentName}
                    onSubmit={handleFeedbackSubmit}
                    isCreator={isCreator}
                    submitText={t("send_feedback")}
                    dialogTitle={t("add_feedback")}
                  />
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      )}
      {exercise && (
        <AttemptSummaryBar
          items={[
            { key: "name", icon: PersonOutlined, label: t("name"), value: createdByName },
            {
              key: "time-spent",
              icon: TimerOutlined,
              label: t("time_spent"),
              value: timeSpent > 0 ? moment.duration(timeSpent).humanize() : "",
            },
            { key: "date", icon: CalendarTodayOutlined, label: t("date"), value: attemptDate },
            { key: "time", icon: AccessTime, label: t("time"), value: attemptTime },
            ...(showsRolePlayed && selectedActor
              ? [
                  {
                    key: "role-played",
                    icon: TheaterComedyOutlined,
                    label: t("role_played"),
                    value: selectedActor.name,
                  },
                ]
              : []),
          ]}
        />
      )}
      {(exercise === "bot" || exercise === "bot-listen-and-speak") &&
        sessionReport?.status && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <SessionReportCard
            sessionReport={sessionReport}
            storageKey={sessionReportStorageKey}
            newStorageKey={sessionReportNewStorageKey}
            corrections={sessionReportCorrections}
            t={t}
          />
        </Box>
      )}
      {feedback && feedbackCreatedDate && feedbackCreaterName ? (
        <>
          <Grid container sx={{ width: "100%" }} spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 12,
              }}
            >
              <Card className={classes.feedbackcard}>
                <CardHeader title={t("feedback")} className={classes.padding} />
                <Divider />
                <ListItem
                  ContainerComponent={"div"}
                  feedbackCreaterName={feedbackCreaterName}
                  feedback={feedback}
                  feedbackCreatedDate={feedbackCreatedDate}
                  userImage={userImage}
                >
                  <ListItemAvatar>
                    {userImage ? (
                      <Avatar
                        className={classes.avatarHeader}
                        src={`${userImage}`}
                        alt=''
                      />
                    ) : (
                      <Avatar
                        className={classes.avatarHeader}
                        sx={(theme) => ({
                          backgroundColor:
                            theme.palette.mode === "dark" ? "white" : "",
                          color:
                            theme.palette.mode === "dark"
                              ? theme.palette.background.default
                              : "",
                        })}
                        alt=''
                      >
                        <Person fontSize='large' />
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={feedbackCreaterName}
                    secondary={feedback}
                  ></ListItemText>
                  <Box className={classes.actionsWrapper}>
                    <Box
                      className={classes.percentageWrapper}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}>
                      <>
                        <Box
                          sx={{
                            display: "inline",
                            flexGrow: 1,
                            ml: 1
                          }}></Box>
                        <Box
                          sx={{
                            mx: 1,
                            fontSize: 14
                          }}>
                          {feedbackCreatedDate === feedbackUpdatedDate
                            ? `Created ${moment(feedbackCreatedDate).fromNow()}`
                            : `Updated ${moment(
                                feedbackUpdatedDate,
                              ).fromNow()}`}
                        </Box>
                        {isCreator ? (
                          <>
                            <IconButton
                              aria-label='update'
                              onClick={handleClickOpen}
                              size='large'
                            >
                              <EditIcon />
                            </IconButton>
                            <UpdateFeedbackDialog
                              initialFeedbackContent={feedback ? feedback : ""}
                              open={open}
                              t={t}
                              handleClose={handleClose}
                              studentName={studentName}
                              onSubmit={handleFeedbackUpdate}
                              submitText={t("update_feedback")}
                              dialogTitle={t("update_feedback")}
                            />
                            <IconButton
                              aria-label='delete'
                              color='error'
                              onClick={deleteFeedback}
                              size='large'
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        ) : (
                          <></>
                        )}
                      </>
                    </Box>
                  </Box>
                </ListItem>
              </Card>
            </Grid>
          </Grid>
        </>
      ) : (
        <></>
      )}
      <Grid container sx={{ width: "100%" }} spacing={2}>
        {!isBot && validAnswerAttempts.length > 0 && (
          <Grid size={12}>
            <Card
              classes={{ root: classes.card }}
              className={classes.fullHeightCard}
            >
              <CardTitleBar title={t("attempt_table")}>
                <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1 }}>
                  <AnswerResultsSummary answerAttempts={validAnswerAttempts} t={t} />
                  {!NO_METHOD_EXERCISES.includes(exercise) && (
                    <AnswerMethodSummary items={validAnswerAttempts} t={t} />
                  )}
                </Box>
              </CardTitleBar>
              <Divider />
              <CardContent>
                <AttemptTable
                  t={t}
                  exercise={exercise}
                  answerAttempts={validAnswerAttempts}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
        {!isBot &&
          ((unansweredQuestions && unansweredQuestions.length > 0) ||
            (unansweredPhrases && unansweredPhrases.length > 0)) && (
            <Grid size={12}>
              <Card
                classes={{ root: classes.card }}
                className={classes.fullHeightCard}
              >
                <CardTitleBar title={t("unanswered_table")} />
                <Divider />
                <CardContent>
                  <UnansweredTable
                    t={t}
                    exercise={exercise}
                    unansweredPhrases={unansweredPhrases}
                    unansweredQuestions={unansweredQuestions}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        {isBot && (
          <Grid size={12} ref={messagesRef}>
            <Card
              classes={{ root: classes.card }}
              className={classes.fullHeightCard}
            >
              <CardTitleBar
                title={t("attempt_messages")}
                actions={
                  isTranscriptPlaying ? (
                    <Button
                      variant='contained'
                      startIcon={<StopCircleIcon />}
                      aria-label='stop'
                      onClick={handleStopTranscript}
                      className={classes.button}
                    >
                      {t("stop")}
                    </Button>
                  ) : (
                    <Button
                      variant='contained'
                      startIcon={<PlayCircleIcon />}
                      aria-label='play'
                      onClick={handlePlayTranscript}
                      className={classes.button}
                    >
                      {t("play")}
                    </Button>
                  )
                }
              >
                <AnswerMethodSummary
                  items={studentMessages}
                  alwaysShow={["text", "voice"]}
                  t={t}
                />
              </CardTitleBar>
              <Divider />
              <CardContent className={classes.contentWrapper}>
                <Chat
                  t={t}
                  messages={messages}
                  viewAttemptMode={true}
                  translationEnabled={"enable"}
                  handleTranslate={handleTranslate}
                  exercise={exercise}
                  isMessagePlaying={isTranscriptPlaying}
                  speak={speak}
                  stopSpeaking={stopSpeaking}
                  isSpeaking={isSpeaking}
                  isSpeakingTextContainer={isSpeakingTextContainer}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      <UpgradeSubscription
        key={"UpgradeSubscription"}
        t={t}
        open={isUpgradeSubscriptionOpen}
        handleClose={handleCloseUpgradeSubcriptionModal}
        onConfirm={handleSubscriptionPage}
        subscription={subscription}
      />
    </div>
  );
}
